import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import WizardContainer from '../../components/Wizard/WizardContainer';
import { ApplicationStatus, LocalKnowledgeAnswers } from '../../types/wizard';
import { usePageTracking, useWizardStepTracking, useStepTimer, useAnalytics } from '../../hooks/useAnalytics';
import { courierApiUrl } from '../../config';
import { getApplicantV2ErrorMessage, loadApplicantV2Session, submitLocalKnowledge } from '../../utils/applicantV2';

interface LocalKnowledgeProps {
  applicationStatus: ApplicationStatus;
}

// Quiz questions - dynamically generated based on delivery area
const getQuizQuestions = (deliveryArea: string) => {
  const deliveryAreaLabel = deliveryArea.replace(/([a-z])([A-Z])/g, '$1 $2');
  return [
    {
      id: 'coverage',
      question: 'Which Valley Eats delivery market are you applying for?',
      options: Array.from(new Set([deliveryAreaLabel, 'Pembroke', 'Arnprior', 'Renfrew', 'Ottawa'])).slice(0, 4),
      correctAnswer: deliveryAreaLabel
    },
    {
      id: 'main-street',
      question: `What makes Valley Eats different from other delivery services?`,
      options: ['Supporting local communities', 'Providing the fastest delivery times', 'Offering the lowest service fees', 'Having the largest restaurant network'],
      correctAnswer: 'Supporting local communities'
    },
    {
      id: 'customer-contact',
      question: 'What should you do if a customer is not answering at delivery?',
      options: [
        'Leave food at door and mark delivered',
        'Call/text customer and wait 5 minutes',
        'Take food back to restaurant',
        'Mark as undeliverable immediately'
      ],
      correctAnswer: 'Call/text customer and wait 5 minutes'
    },
    {
      id: 'comfort-level',
      question: `How comfortable are you navigating ${deliveryAreaLabel}?`,
      options: ['Not comfortable', 'Somewhat comfortable', 'Comfortable', 'Very comfortable'],
      correctAnswer: 'Very comfortable'
    },
    {
      id: 'areas-known',
      question: `Which areas of ${deliveryAreaLabel} do you know best?`,
      options: ['Downtown only', 'East end only', 'West end only', 'All areas'],
      correctAnswer: 'All areas'
    },
    {
      id: 'delivery-time',
      question: 'What is the ideal time to complete a typical food delivery?',
      options: ['5-10 minutes', '15-25 minutes', '30-40 minutes', '45+ minutes'],
      correctAnswer: '15-25 minutes'
    },
    {
      id: 'cold-food',
      question: 'What should you do if you receive an order and the food feels cold?',
      options: [
        'Deliver it anyway',
        'Ask the restaurant to remake it',
        'Contact support before leaving',
        'Cancel the order'
      ],
      correctAnswer: 'Contact support before leaving'
    },
    {
      id: 'multiple-orders',
      question: 'Can you deliver multiple orders at once?',
      options: [
        'Yes, always combine orders',
        'Yes, if app assigns them together',
        'No, one at a time only',
        'Only if customers agree'
      ],
      correctAnswer: 'Yes, if app assigns them together'
    },
    {
      id: 'weather',
      question: 'During bad weather, what should you prioritize?',
      options: [
        'Speed to earn more',
        'Safety first, slower delivery',
        'Skip deliveries',
        'Only accept close orders'
      ],
      correctAnswer: 'Safety first, slower delivery'
    },
    {
      id: 'parking',
      question: 'Best practice when picking up from a busy restaurant?',
      options: [
        'Park in fire lane for quick pickup',
        'Find legal parking even if farther',
        'Double park with hazards on',
        'Have restaurant bring food out'
      ],
      correctAnswer: 'Find legal parking even if farther'
    }
  ];
};

const LocalKnowledge: React.FC<LocalKnowledgeProps> = ({ applicationStatus }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || searchParams.get('email') || '';

  const questions = getQuizQuestions(applicationStatus.deliveryArea || 'Pembroke');

  const [answers, setAnswers] = useState<LocalKnowledgeAnswers>({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(600); // 10 minutes in seconds
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  usePageTracking('Local Knowledge');
  useWizardStepTracking('local-knowledge', 2, 7);
  const { getTimeSpent } = useStepTimer();
  const { trackQuizStart, trackQuizComplete, trackWizardStepComplete, trackAPIError } = useAnalytics();
  const quizStarted = useRef(false);
  const submitRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    if (!quizStarted.current) {
      trackQuizStart('v1');
      quizStarted.current = true;
    }
  }, [trackQuizStart]);

  // Timer countdown
  useEffect(() => {
    if (isSubmitting) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          submitRef.current();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isSubmitting]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswer = (questionId: string, answer: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const v2Session = loadApplicantV2Session();
      const response = v2Session
        ? await submitLocalKnowledge(v2Session, answers)
        : await axios.post(courierApiUrl('/application/local-knowledge'), {
            token,
            answers,
            timeSpent: 600 - timeRemaining
          });

      if (response.data.success) {
        const correctCount = questions.filter(q => answers[q.id] === q.correctAnswer).length;
        trackQuizComplete({ score: Math.round((correctCount / questions.length) * 100), correctAnswers: correctCount, incorrectAnswers: questions.length - correctCount, timeTaken: 600 - timeRemaining });
        trackWizardStepComplete({ stepName: 'local-knowledge', timeOnStep: getTimeSpent(), completionStatus: 'success' });
        // Navigate immediately to video introduction
        navigate(response.data.applicationStatus === 'NOT_APPROVED'
          ? '/wizard/approved'
          : `/wizard/video-intro${v2Session ? '' : `?token=${token}`}`);
      } else {
        setError(response.data.error || 'Failed to submit quiz');
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error('Knowledge check error:', err);
      trackAPIError({ endpoint: '/api/application/local-knowledge', errorCode: (err as any).response?.status || 'unknown', errorMessage: (err as any).message });
      setError(getApplicantV2ErrorMessage(err, 'An error occurred. Please try again.'));
      setIsSubmitting(false);
    }
  };
  submitRef.current = handleSubmit;

  const isQuestionAnswered = (index: number) => {
    return !!answers[questions[index].id];
  };

  const allAnswered = questions.every(q => !!answers[q.id]);

  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <WizardContainer
      currentStepId="local-knowledge"
      applicationStatus={applicationStatus}
    >
      <div className="max-w-3xl mx-auto">
        <div className="card p-6 sm:p-8">
          {/* Header with timer */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-charcoal">
                Local Knowledge Check
              </h1>
              <p className="text-sm text-gray-600">
                Question {currentQuestion + 1} of {questions.length}
              </p>
            </div>
            <div className={`text-2xl font-bold ${timeRemaining < 60 ? 'text-alert-red' : 'text-logo-green'}`}>
              ⏱️ {formatTime(timeRemaining)}
            </div>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 mb-8">
            <div
              className="bg-logo-green h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Question */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-charcoal mb-6">
              {question.question}
            </h2>

            <div className="space-y-3">
              {question.options.map((option) => (
                <button
                  key={option}
                  onClick={() => handleAnswer(question.id, option)}
                  className={`
                    selection-card w-full p-4 text-left
                    ${answers[question.id] === option
                      ? 'selection-card-selected font-semibold'
                      : 'text-charcoal'}
                  `}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          {/* Navigation */}
          {currentQuestion < questions.length - 1 ? (
            <div className="flex justify-between items-center">
              <button
                onClick={handlePrevious}
                disabled={currentQuestion === 0}
                className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ←<span className="hidden sm:inline"> Previous</span>
              </button>

              <div className="flex gap-1">
                {questions.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full ${
                      isQuestionAnswered(index)
                        ? 'bg-logo-green'
                        : index === currentQuestion
                        ? 'bg-logo-green/50'
                        : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={handleNext}
                disabled={!isQuestionAnswered(currentQuestion)}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="hidden sm:inline">Next </span>→
              </button>
            </div>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!allAnswered || isSubmitting}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Quiz'}
            </button>
          )}

          {error && (
            <div className="mt-6 bg-alert-red/10 border border-alert-red text-alert-red rounded-lg p-4">
              {error}
            </div>
          )}
        </div>
      </div>
    </WizardContainer>
  );
};

export default LocalKnowledge;
